import { useAuth } from '../context/AuthContext';

// Modules a property can switch off (super admin, Properties page).
// Keep in sync with FEATURE_KEYS in backend/tenancy.py.
export const FEATURES = [
  { key: 'dashboard', label: 'Dashboard', hint: 'Revenue and bookings overview' },
  { key: 'bookings', label: 'Bookings', hint: 'Ad bookings' },
  { key: 'orders', label: 'Orders', hint: 'Multi-item orders and packages' },
  { key: 'editions', label: 'Editions & Flatplan', hint: 'Print editions and page layout' },
  { key: 'digital', label: 'Digital', hint: 'Website / email / social calendar' },
  { key: 'accounting', label: 'Accounting', hint: 'Invoices, receipts, facturas' },
  { key: 'ad_matrix', label: 'Ad Matrix', hint: 'Advertisers by edition grid' },
];

// Settings sections (by their data-testid) that only make sense when a
// feature is on. Hidden when it is off.
const SETTINGS_SECTION_FEATURE = {
  'page-dimensions-section': 'editions',
  'invoice-email-template-section': 'accounting',
  'default-rate-section': 'accounting',
  'product-types-section': 'bookings',
  'packages-section': 'orders',
};

// Settings sections that only exist for a multi-currency (USD + MXN) property.
const MULTI_CURRENCY_SECTIONS = ['default-rate-section'];

const singular = (label) => (label.length > 1 && /s$/i.test(label) ? label.slice(0, -1) : label);

// Reads the active property's settings from the logged-in user (/auth/me).
// Missing data means "everything on", so nothing disappears while loading.
export const useFeatures = () => {
  const { user } = useAuth();
  const prop = (user && user.property) || {};
  const flags = prop.features || {};
  const on = (key) => flags[key] !== false;
  const clientsLabel = prop.client_label || 'Advertisers';
  const isSalesRep = user?.role === 'sales_rep';
  // USD + MXN (DOF rates, MXN totals, "Pay in MXN"). Missing means on.
  const multiCurrency = prop.multi_currency !== false;
  return {
    on,
    clientsLabel,                       // "Advertisers" / "Clients"
    clientLabel: singular(clientsLabel), // "Advertiser" / "Client"
    // A sales-team property: no bookings and no accounting.
    salesOnly: !on('bookings') && !on('accounting'),
    repVisibility: prop.rep_client_visibility || 'all',
    multiCurrency,
    // Where "/" goes: the Dashboard when this user has one, else Sales.
    homePath: on('dashboard') && !isSalesRep ? '/dashboard' : '/sales',
    settingsSectionHidden: (testid) => {
      if (!multiCurrency && MULTI_CURRENCY_SECTIONS.includes(testid)) return true;
      const feature = SETTINGS_SECTION_FEATURE[testid];
      return !!feature && !on(feature);
    },
  };
};
